import { NotificationsNone } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  ListItemIcon,
  ListItemText,
  Stack,
  Switch
} from '@mui/material'
import Badge from '@mui/material/Badge'
import IconButton from '@mui/material/IconButton'
import MuiMenu from '@mui/material/Menu'
import MuiMenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { alpha, styled } from '@mui/material/styles'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { Fragment, type MouseEvent, useEffect, useRef, useState } from 'react'
import { Virtuoso } from 'react-virtuoso'
import { type Settings } from 'src/@core/context/settingsContext'

import Center from '~/components/Center'
import { OPEN_NOTIFICATIONS_EVENT } from '~/components/KeyboardShortcuts/constants'
import { useOverlay } from '~/components/Overlay'
import { useLocalStorageState } from '~/hooks/useLocalStorageState'
import routeDirectory from '~/navigation/routes'

import useMarkNotificationRead from '../useMarkNotificationRead'
import { type DisplayNotification, useMergedNotifications } from '../useMergedNotifications'
import MergedNotificationDetailOverlay from './MergedNotificationDetailOverlay'
import NotificationsActionsMenu from './NotificationsActionsMenu'
import PriorityPill from './PriorityPill'

type Props = {
  settings: Settings
}

const MAX_BADGE_COUNT = 99
const ROW_HEIGHT = 61
const MAX_LIST_HEIGHT = 350

// The paper and list form a flex column so that when MUI clamps the paper to
// the viewport on short windows, the Virtuoso list is what shrinks — the
// header and the "view all" footer stay visible instead of being clipped by
// the paper's hidden overflow.
const Menu = styled(MuiMenu)(({ theme }) => ({
  '& .MuiMenu-paper': {
    backgroundColor: theme.palette.background.navCard,
    width: 380,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  '& .MuiMenu-list': {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0
  }
}))

const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${theme.palette.divider}`
  }
}))

const MenuItemTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  flex: '1 1 100%',
  overflow: 'hidden',
  fontSize: '0.875rem',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  marginBottom: theme.spacing(0.75)
}))

const MenuItemSubtitle = styled(Typography)({
  flex: '1 1 100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis'
})

/**
 * Notifications bell. Its unread count and dropdown list cover Workflows in-app
 * notifications. Clicking a row marks it read and opens the shared detail
 * overlay. Alt/Option-clicking a row flips its read state without opening it,
 * leaving the menu open. Read rows stay listed, dimmed, rather than
 * disappearing from the dropdown; a header switch filters the list down to
 * unread rows and a header overflow menu clears them all at once.
 */
export default function NotificationsBell({ settings }: Props) {
  const t = useTranslations('notifications.Dropdown')
  const tCommon = useTranslations('notifications.common')
  const { mode } = settings

  const [anchorEl, setAnchorEl] = useState<Element>()
  const bellRef = useRef<HTMLButtonElement>(null)
  const router = useRouter()

  // Global (not per-page) and per-user, like the timezone-banner snooze: the
  // bell renders on every page, and the preference follows the account.
  const [unreadOnly, setUnreadOnly] = useLocalStorageState('notifications-unread-only', {
    defaultValue: false,
    ignorePath: true,
    userSpecific: true
  })
  const { rows, unreadCount, isLoading } = useMergedNotifications({ scope: 'bell', unreadOnly, count: 50 })
  const { markRead, toggleRead } = useMarkNotificationRead()

  const detailOverlay = useOverlay()
  const [selected, setSelected] = useState<DisplayNotification>()

  useEffect(() => {
    const handleOpen = () => {
      if (bellRef.current) setAnchorEl(bellRef.current)
    }
    document.addEventListener(OPEN_NOTIFICATIONS_EVENT, handleOpen)
    return () => document.removeEventListener(OPEN_NOTIFICATIONS_EVENT, handleOpen)
  }, [])

  const handleClose = () => setAnchorEl(undefined)

  const handleRowClick = (notification: DisplayNotification, event: MouseEvent<HTMLElement>) => {
    // Alt/Option-click flips the row in place: the menu stays open and the
    // detail overlay stays closed so the user can work down the list.
    if (event.altKey) {
      toggleRead(notification)
      return
    }

    markRead(notification)
    setSelected(notification)
    detailOverlay.openOverlay()
    handleClose()
  }

  return (
    <Fragment>
      <IconButton
        ref={bellRef}
        color="inherit"
        aria-label={tCommon('notifications')}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        aria-controls={anchorEl ? 'notifications-menu' : undefined}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <Badge
          color="error"
          overlap="circular"
          badgeContent={unreadCount}
          max={MAX_BADGE_COUNT}
          sx={{
            '& .MuiBadge-badge': {
              boxShadow: (theme) => `0 0 0 2px ${theme.palette.background.paper}`
            }
          }}
        >
          <NotificationsNone />
        </Badge>
      </IconButton>
      <Menu
        id="notifications-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        transitionDuration={0}
        MenuListProps={{
          onKeyDown: (e) => {
            if (e.key === 'Tab') {
              e.stopPropagation()
            }
          }
        }}
      >
        <MenuItem
          disableRipple
          disableTouchRipple
          // MUI's Menu intercepts Tab on the MenuList to close the menu, and in
          // v6 that runs before MenuListProps.onKeyDown (see CommissioningFilter).
          // Stopping propagation at the item lets Tab move focus into and out of
          // the unread-only switch instead of closing the menu.
          onKeyDown={(event) => {
            if (event.key === 'Tab') event.stopPropagation()
          }}
          sx={{ cursor: 'default', userSelect: 'auto', backgroundColor: 'transparent !important', flexShrink: 0 }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography sx={{ cursor: 'text', fontWeight: 600 }}>{tCommon('notifications')}</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormControlLabel
                control={
                  <Switch size="small" checked={unreadOnly} onChange={(event) => setUnreadOnly(event.target.checked)} />
                }
                label={t('unreadOnly')}
                labelPlacement="start"
                slotProps={{ typography: { variant: 'caption', color: 'text.secondary', sx: { mr: 1 } } }}
                sx={{ mr: 0, ml: 0 }}
              />
              <NotificationsActionsMenu unreadCount={unreadCount} />
            </Stack>
          </Box>
        </MenuItem>
        <Box
          sx={{
            // Floored at one row even when empty: the Virtuoso child is sized
            // `height: '100%'`, and a percentage resolves against the specified
            // height — a `minHeight` here would not reach it, collapsing the
            // spinner/empty placeholder.
            height: Math.max(rows.length, 1) * ROW_HEIGHT,
            minHeight: ROW_HEIGHT,
            maxHeight: MAX_LIST_HEIGHT,
            flexShrink: 1
          }}
        >
          <Virtuoso
            components={{
              EmptyPlaceholder: () => (
                <Center.Middle>
                  {isLoading ? (
                    <CircularProgress />
                  ) : (
                    <Typography variant="body2">{t('noNewNotifications')}</Typography>
                  )}
                </Center.Middle>
              ),
              Item: ({ children, ...props }) => {
                // Put the click handler on the MenuItem (a ButtonBase) itself so
                // keyboard Enter/Space activate the focused row — MUI translates
                // those keys into an onClick on the ButtonBase, but not on nested
                // elements. Virtuoso exposes the row's data index on props.
                const notification = rows[props['data-item-index']]
                return (
                  <MenuItem
                    {...props}
                    divider
                    disableRipple
                    onClick={(event) => notification && handleRowClick(notification, event)}
                    sx={{
                      py: 2,
                      cursor: 'pointer',
                      // Unread rows carry the theme's selected-state primary
                      // tint, nudged darker on hover so the highlight survives
                      // the hover overlay.
                      ...(notification &&
                        !notification.read && {
                          backgroundColor: (theme) =>
                            alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
                          '&:hover': {
                            backgroundColor: (theme) =>
                              alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity * 1.5)
                          }
                        })
                    }}
                  >
                    {children}
                  </MenuItem>
                )
              }
            }}
            style={{ height: '100%' }}
            data={rows}
            itemContent={(_, notification) => <NotificationMenuItem notification={notification} />}
          />
        </Box>
        <MenuItem
          disableRipple
          disableTouchRipple
          sx={{
            py: 3.5,
            borderBottom: 0,
            flexShrink: 0,
            cursor: 'default',
            userSelect: 'auto',
            backgroundColor: 'transparent !important',
            borderTop: (theme) => `1px solid ${theme.palette.divider}`
          }}
        >
          <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: mode === 'dark' ? 'accent.light' : 'accent.main',
              '&:hover': {
                backgroundColor: mode === 'dark' ? 'accent.main' : 'accent.dark'
              }
            }}
            onClick={() => {
              router.push(routeDirectory.notifications)
              handleClose()
            }}
          >
            {t('viewAllNotifications')}
          </Button>
        </MenuItem>
      </Menu>
      <MergedNotificationDetailOverlay {...detailOverlay} notification={selected} />
    </Fragment>
  )
}

type NotificationMenuItemProps = {
  notification: DisplayNotification
}

function NotificationMenuItem({ notification }: NotificationMenuItemProps) {
  return (
    <Fragment>
      <ListItemIcon sx={{ mr: 2 }}>
        <PriorityPill priority={notification.priority} />
      </ListItemIcon>
      <ListItemText>
        <Stack>
          <MenuItemTitle sx={{ ...(notification.read && { color: 'text.secondary', fontWeight: 400 }) }}>
            {notification.title}
          </MenuItemTitle>
          {notification.body ? (
            <MenuItemSubtitle variant="caption" color="text.secondary">
              {notification.body}
            </MenuItemSubtitle>
          ) : null}
        </Stack>
      </ListItemText>
    </Fragment>
  )
}
